import { getCurrentUser, loginWithGoogle as loginWithGoogleApi, logout as logoutApi } from "@/api/auth";
import { useAuthStore } from "@/app/store/useAuthStore";
import type { GrantRules, PageGrantLevel } from "@/shared/types";
import { ReactNode, useCallback, useEffect } from "react";
import { AuthContext } from "./AuthContext";
interface AuthProviderProps {
  children: ReactNode;
}

function normalizePageGrantLevel(v: unknown): PageGrantLevel {
  return v === "reader" || v === "writer" ? v : "none";
}

function getLevel(grant: GrantRules, pageName: string): PageGrantLevel {
  const direct = normalizePageGrantLevel(grant?.[pageName]);
  if (direct !== "none") return direct;
  return normalizePageGrantLevel(grant?.["all"]);
}



export function AuthProvider({ children }: AuthProviderProps) {
  const { user, grant, isLoading, setUser, setGrant, setLoading, clearAuth } = useAuthStore();
  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (token) {
          const me = await getCurrentUser();
          const profile = me.user?.profile ?? null;
          const nextGrant = (me.user?.grant ?? {}) as GrantRules;

          setUser(profile);
          setGrant(nextGrant);
        }
      } catch (error) {
        console.error("인증 초기화 실패:", error);
        clearAuth();
      } finally {
        setLoading(false);
      }
    };

    void initAuth();
  }, [setUser, setGrant, setLoading, clearAuth]);

  const loginWithGoogleCallBack = useCallback(
    async (idToken: string) => {
      setLoading(true);
      try {
        const response = await loginWithGoogleApi({ token: idToken, tokenType: "id_token" });
        localStorage.setItem("access_token", response.accessToken);

        // 로그인 응답 shape이 어떻게 오든, /me로 최종 동기화해서 grant까지 확보
        const me = await getCurrentUser();
        const profile = me.user?.profile ?? null;
        const nextGrant = (me.user?.grant ?? {}) as GrantRules;

        setUser(profile);
        setGrant(nextGrant);
      } catch (error) {
        console.error("Google 로그인 실패:", error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setUser, setGrant]
  );

  const getPageGrantLevel = useCallback((pageName: string) => getLevel(grant, pageName), [grant]);
  const canReadPage = useCallback((pageName: string) => getLevel(grant, pageName) !== "none", [grant]);
  const canWritePage = useCallback((pageName: string) => getLevel(grant, pageName) === "writer", [grant]);


  const logout = useCallback(async () => {
    // 토큰을 미리 변수에 저장
    const token = localStorage.getItem("access_token");

    try {
      // 백엔드에 로그아웃 요청
      if (token) {
        await logoutApi();
      }
    } catch (error) {
      console.error("로그아웃 API 호출 실패:", error);
    } finally {
      // 로컬 스토리지 정리
      localStorage.removeItem("access_token");
      localStorage.removeItem("google_access_token");
      clearAuth();
    }
  }, [clearAuth]);

  return (
    <AuthContext.Provider
      value={{
        user,
        grant,
        isAuthenticated: !!user,
        isLoading,
        getPageGrantLevel,
        canReadPage,
        canWritePage,
        loginWithGoogleCallBack,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
